import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Minus, Pencil, Check, X } from 'lucide-react';
import { useLayoutEffect, useRef, useState } from 'react';
import { get, put } from '@/api/client';
import { NotFoundPage } from '@/pages/errors/NotFoundPage';
import { Button } from '@/components/atoms/Button';
import { Input } from '@/components/atoms/Input';
import { Textarea } from '@/components/atoms/Textarea';
import { Skeleton } from '@/components/atoms/Skeleton';
import { RateText } from '@/components/atoms/RateText';
import { PriceText } from '@/components/atoms/PriceText';
import { comma, isoDate } from '@/lib/format';
import { cn } from '@/lib/cn';
import { COIN_NAMES_KR, stripKrw } from '@/constants/coins';
import { toast } from '@/store/toastStore';

interface Position {
  ticker: string;
  asset_type: 'stock' | 'coin';
  ticker_name: string | null;
  market: string | null;
  total_buy_qty: number;
  total_buy_amount: number;
  avg_buy_price: number | null;
  total_sell_qty: number;
  total_sell_amount: number;
  avg_sell_price: number | null;
  holding_qty: number;
  realized_pnl: number | null;
  realized_pnl_rate: number | null;
  tx_count: number;
  buy_tx_count: number;
  sell_tx_count: number;
  first_tx_date: string | null;
  last_tx_date: string | null;
  first_buy_date: string | null;
  last_sell_date: string | null;
  status: 'holding' | 'closed' | 'sell_only';
}

interface Transaction {
  record_id: number;
  type: 'buy' | 'sell';
  price: number;
  quantity: number;
  date: string;
  amount: number;
  memo: string | null;
}

/**
 * 종목별 포지션 상세 페이지
 * URL: /diary/ticker/:ticker
 */
export function DiaryPositionPage() {
  const { ticker } = useParams<{ ticker: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  /**
   * 인라인 편집 상태 — record_id + type(buy/sell) 조합으로 유일 식별
   * 이 포지션 페이지의 타임라인은 1 record → 1 또는 2 이벤트로 펼쳐지므로
   * 편집 시 어느 leg 를 수정하는지 type 으로 구분 필요.
   */
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<{
    price: string;
    quantity: string;
    memo: string;
  }>({
    price: '',
    quantity: '',
    memo: '',
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['diary-position', ticker],
    queryFn: () =>
      get<{ position: Position; transactions: Transaction[] }>(
        `/diaries/positions/${encodeURIComponent(ticker!)}`,
      ),
    enabled: !!ticker,
    retry: (count, err) => {
      const status = (err as any)?.response?.status;
      if (status === 404) return false;
      return count < 2;
    },
  });

  /* 존재하지 않는 종목 코드 — 404 시 NotFoundPage 렌더링 (아래 분기) */
  const isPositionNotFound = (error as any)?.response?.status === 404;

  /** 거래 내역 수정 — type 에 따라 buy/sell 필드만 선택적으로 PUT */
  const updateMutation = useMutation({
    mutationFn: (params: {
      recordId: number;
      type: 'buy' | 'sell';
      price: number;
      quantity: number;
      memo: string;
    }) => {
      const body: Record<string, number | string> = {
        quantity: params.quantity,
        // 빈 문자열은 NULL 로 저장되도록 명시적으로 빈 문자열 전송
        memo: params.memo,
      };
      if (params.type === 'buy') body.buyPrice = params.price;
      else body.sellPrice = params.price;
      return put(`/diaries/${params.recordId}`, body);
    },
    onSuccess: () => {
      setEditingKey(null);
      queryClient.invalidateQueries({ queryKey: ['diary-position', ticker] });
      queryClient.invalidateQueries({ queryKey: ['diaries', 'positions'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error?.message ?? '수정에 실패했어요.');
    },
  });

  const txKey = (tx: Transaction) => `${tx.record_id}-${tx.type}`;

  const startEdit = (tx: Transaction) => {
    setEditingKey(txKey(tx));
    setEditForm({
      price: String(tx.price),
      quantity: String(tx.quantity),
      memo: tx.memo ?? '',
    });
  };

  const cancelEdit = () => {
    setEditingKey(null);
  };

  const saveEdit = (tx: Transaction) => {
    const price = Number(editForm.price);
    const quantity = Number(editForm.quantity);
    if (!price || price <= 0 || !quantity || quantity <= 0) {
      toast.error('가격과 수량은 0보다 커야 해요.');
      return;
    }
    updateMutation.mutate({
      recordId: tx.record_id,
      type: tx.type,
      price,
      quantity,
      memo: editForm.memo.trim(),
    });
  };

  if (isPositionNotFound) {
    return <NotFoundPage />;
  }

  if (isLoading) {
    return (
      <div className="container-base py-10">
        <Skeleton className="h-10 w-48 mb-4" />
        <Skeleton className="h-32 w-full mb-6" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (!data) return null;

  const { position: p, transactions } = data;
  /* 종목명: COIN_NAMES_KR → ticker_name → stripKrw(코인) → 코드 */
  const isCoin = p.asset_type === 'coin';
  const displayName =
    (isCoin ? COIN_NAMES_KR[p.ticker] : null) ??
    p.ticker_name ??
    (isCoin ? stripKrw(p.ticker) : p.ticker);
  const showCodeChip = displayName !== p.ticker;

  const statusBadge =
    p.status === 'holding'
      ? {
          label: '보유 중',
          cls: 'bg-[var(--color-primary)]/10 text-[var(--color-primary)]',
        }
      : p.status === 'closed'
      ? {
          label: '청산',
          cls: 'bg-[var(--color-text-sub)]/10 text-[var(--color-text-sub)]',
        }
      : {
          label: '매도 선행',
          cls: 'bg-[var(--color-fall)]/10 text-[var(--color-fall)]',
        };

  return (
    <div className="container-base py-10">
      {/* 종목 헤더 */}
      <section className="mb-6 rounded-2xl border border-[var(--color-border)] bg-white p-6 sm:p-8">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-2xl">{p.asset_type === 'coin' ? '🪙' : '📈'}</span>
          <h1 className="text-3xl font-extrabold">{displayName}</h1>
          {showCodeChip && (
            <span className="rounded bg-[var(--color-bg)] px-2 py-0.5 font-mono text-xs text-[var(--color-text-sub)]">
              {p.ticker}
            </span>
          )}
          {p.market && (
            <span className="text-xs text-[var(--color-text-sub)]">{p.market}</span>
          )}
          <span
            className={`ml-auto rounded-full px-3 py-1 text-xs font-semibold ${statusBadge.cls}`}
          >
            {statusBadge.label}
          </span>
        </div>

        {/* 집계 통계 4칸 */}
        <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-4">
          <SummaryCell
            label="평균 매수가"
            value={
              p.avg_buy_price !== null ? (
                `${comma(Math.round(p.avg_buy_price))}원`
              ) : (
                <span className="text-[var(--color-text-sub)]">—</span>
              )
            }
            sub={
              p.total_buy_qty > 0
                ? `${p.total_buy_qty}주 / ${comma(Math.round(p.total_buy_amount))}원`
                : undefined
            }
          />
          <SummaryCell
            label="평균 매도가"
            value={
              p.avg_sell_price !== null ? (
                `${comma(Math.round(p.avg_sell_price))}원`
              ) : (
                <span className="text-[var(--color-text-sub)]">—</span>
              )
            }
            sub={
              p.total_sell_qty > 0
                ? `${p.total_sell_qty}주 / ${comma(Math.round(p.total_sell_amount))}원`
                : undefined
            }
          />
          <SummaryCell
            label="보유 수량"
            value={
              p.holding_qty > 0 ? (
                <span className="text-[var(--color-primary)]">{p.holding_qty}주</span>
              ) : (
                `${p.holding_qty}주`
              )
            }
            sub={p.holding_qty > 0 ? '매도하면 실현손익 계산' : '전량 매도'}
          />
          <SummaryCell
            label="실현 손익"
            value={
              p.realized_pnl !== null ? (
                <PriceText
                  value={p.realized_pnl}
                  trend={p.realized_pnl >= 0 ? 'up' : 'down'}
                />
              ) : (
                <span className="text-[var(--color-text-sub)]">—</span>
              )
            }
            sub={
              p.realized_pnl_rate !== null ? (
                <RateText value={p.realized_pnl_rate} size="sm" />
              ) : undefined
            }
          />
        </div>

        {/* 액션 버튼 */}
        <div className="mt-6 flex gap-2">
          <Button
            fullWidth
            onClick={() =>
              navigate(
                `/diary/new?ticker=${encodeURIComponent(p.ticker)}&type=buy`,
              )
            }
          >
            <Plus className="h-4 w-4" />
            매수 추가
          </Button>
          <Button
            fullWidth
            variant="secondary"
            onClick={() =>
              navigate(
                `/diary/new?ticker=${encodeURIComponent(p.ticker)}&type=sell`,
              )
            }
          >
            <Minus className="h-4 w-4" />
            매도 추가
          </Button>
        </div>
      </section>

      {/* 거래 타임라인 */}
      <section className="rounded-2xl border border-[var(--color-border)] bg-white p-6 sm:p-8">
        <h2 className="mb-4 section-title">거래 내역</h2>

        <div className="relative">
          {/* 세로선 */}
          {transactions.length > 0 && (
            <div className="absolute bottom-4 left-3 top-4 w-0.5 bg-[var(--color-border)]" />
          )}

          <ul className="space-y-4">
            {transactions.map((tx) => {
              const isEditing = editingKey === txKey(tx);
              const newAmount =
                isEditing && editForm.price && editForm.quantity
                  ? Number(editForm.price) * Number(editForm.quantity)
                  : 0;

              return (
                <li key={txKey(tx)} className="relative flex gap-4">
                  {/* 점 */}
                  <div
                    className={`relative z-10 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full ${
                      tx.type === 'buy'
                        ? 'bg-[var(--color-rise)]/10 text-[var(--color-rise)]'
                        : 'bg-[var(--color-fall)]/10 text-[var(--color-fall)]'
                    }`}
                  >
                    {tx.type === 'buy' ? (
                      <Plus className="h-3.5 w-3.5" />
                    ) : (
                      <Minus className="h-3.5 w-3.5" />
                    )}
                  </div>

                  {/* 거래 내용 */}
                  <div className="flex-1 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span
                            className={`text-xs font-bold ${
                              tx.type === 'buy'
                                ? 'text-[var(--color-rise)]'
                                : 'text-[var(--color-fall)]'
                            }`}
                          >
                            {tx.type === 'buy' ? '매수' : '매도'}
                          </span>
                          <span className="text-xs text-[var(--color-text-sub)]">
                            {isoDate(tx.date)}
                          </span>
                        </div>

                        {isEditing ? (
                          /* ── 편집 모드 ─────────────────── */
                          <div className="mt-2 space-y-2">
                            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                              <div>
                                <label className="mb-0.5 block text-[10px] text-[var(--color-text-sub)]">
                                  {tx.type === 'buy' ? '매수가' : '매도가'}
                                </label>
                                <Input
                                  type="number"
                                  value={editForm.price}
                                  onChange={(e) =>
                                    setEditForm({
                                      ...editForm,
                                      price: e.target.value,
                                    })
                                  }
                                  className="h-8 text-sm"
                                />
                              </div>
                              <div>
                                <label className="mb-0.5 block text-[10px] text-[var(--color-text-sub)]">
                                  수량
                                </label>
                                <Input
                                  type="number"
                                  value={editForm.quantity}
                                  onChange={(e) =>
                                    setEditForm({
                                      ...editForm,
                                      quantity: e.target.value,
                                    })
                                  }
                                  className="h-8 text-sm"
                                />
                              </div>
                            </div>
                            {newAmount > 0 && (
                              <div className="text-xs text-[var(--color-text-sub)]">
                                총 금액:{' '}
                                <strong className="text-[var(--color-text)]">
                                  {comma(Math.round(newAmount))}원
                                </strong>
                              </div>
                            )}
                            {/* 메모 편집 */}
                            <div>
                              <label className="mb-0.5 block text-[10px] text-[var(--color-text-sub)]">
                                메모
                              </label>
                              <Textarea
                                value={editForm.memo}
                                onChange={(e) =>
                                  setEditForm({
                                    ...editForm,
                                    memo: e.target.value,
                                  })
                                }
                                rows={3}
                                maxLength={2000}
                                placeholder="매매 이유, 배운 점 등 (선택)"
                                className="w-full rounded border border-[var(--color-border)] bg-[var(--color-surface)] p-2 text-xs focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/40"
                              />
                              <div className="mt-0.5 text-right text-[10px] text-[var(--color-text-sub)]">
                                {editForm.memo.length}/2000
                              </div>
                            </div>
                          </div>
                        ) : (
                          /* ── 일반 모드 ─────────────────── */
                          <>
                            <div className="mt-1 text-sm">
                              <strong>{comma(Math.round(tx.price))}원</strong>
                              <span className="text-[var(--color-text-sub)]">
                                {' '}× {tx.quantity}
                              </span>
                              <span className="ml-2 text-[var(--color-text-sub)]">
                                = {comma(Math.round(tx.amount))}원
                              </span>
                            </div>
                            {/* 메모 표시 (항상 영역 표시) */}
                            <MemoDisplay memo={tx.memo} />
                          </>
                        )}
                      </div>

                      {/* 액션 버튼 — 편집 중이면 저장/취소, 아니면 편집 */}
                      <div className="flex flex-shrink-0 gap-1">
                        {isEditing ? (
                          <>
                            <button
                              onClick={() => saveEdit(tx)}
                              disabled={updateMutation.isPending}
                              className="rounded p-1 text-[var(--color-primary)] hover:bg-[var(--color-primary)]/10 disabled:opacity-50"
                              aria-label="저장"
                            >
                              <Check className="h-4 w-4" />
                            </button>
                            <button
                              onClick={cancelEdit}
                              disabled={updateMutation.isPending}
                              className="rounded p-1 text-[var(--color-text-sub)] hover:bg-[var(--color-bg)] disabled:opacity-50"
                              aria-label="취소"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => startEdit(tx)}
                            className="rounded p-1 text-[var(--color-text-sub)] hover:bg-[var(--color-primary)]/10 hover:text-[var(--color-primary)]"
                            aria-label="거래 수정"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>

          {transactions.length === 0 && (
            <p className="py-8 text-center text-sm text-[var(--color-text-sub)]">
              거래 내역이 없어요.
            </p>
          )}
        </div>
      </section>
    </div>
  );
}

function SummaryCell({
  label,
  value,
  sub,
}: {
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
}) {
  return (
    <div className="rounded-lg bg-[var(--color-bg)] p-3">
      <div className="text-xs text-[var(--color-text-sub)]">{label}</div>
      <div className="mt-1 text-base font-bold">{value}</div>
      {sub && (
        <div className="mt-0.5 text-[10px] text-[var(--color-text-sub)]">{sub}</div>
      )}
    </div>
  );
}

/**
 * 메모 표시 컴포넌트
 * - 메모 없으면 "메모 없음" 회색 표시
 * - 메모 있으면 line-clamp-3 로 축약
 * - 실제로 3줄 초과할 때만 [더보기]/[접기] 버튼 노출 (scrollHeight 측정)
 * - 자체 상태로 확장 여부 관리 → 거래 항목별 독립 펼치기/접기
 */
function MemoDisplay({ memo }: { memo: string | null }) {
  const [expanded, setExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);

  useLayoutEffect(() => {
    const el = textRef.current;
    if (!el || expanded) return;
    // line-clamp-3 적용 상태에서 scrollHeight vs clientHeight 비교
    // scrollHeight = 전체 컨텐츠 높이, clientHeight = 실제 보이는 높이
    setIsOverflowing(el.scrollHeight - el.clientHeight > 1);
  }, [memo, expanded]);

  if (!memo || memo.trim() === '') {
    return (
      <p className="mt-2 text-xs italic text-[var(--color-text-sub)]/60">메모 없음</p>
    );
  }

  return (
    <div className="mt-2">
      <p
        ref={textRef}
        className={cn(
          'whitespace-pre-wrap text-xs text-[var(--color-text-sub)]',
          !expanded && 'line-clamp-3',
        )}
      >
        {memo}
      </p>
      {(isOverflowing || expanded) && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((v) => !v);
          }}
          className="mt-1 text-xs font-medium text-[var(--color-primary)] hover:underline"
        >
          {expanded ? '접기' : '더보기'}
        </button>
      )}
    </div>
  );
}
